import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2, ChevronDown, ChevronRight, Plus, Trash2,
  RotateCcw, Save, HardHat,
} from 'lucide-react';

/**
 * Gestion des questions tarifaires envoyées aux prospects lors de l'onboarding.
 * Une liste par corps de métier — chaque ligne = un poste tarifé (label + unité).
 * Les défauts sont proposés, l'admin peut ajouter/modifier/supprimer et réinitialiser.
 */
export default function TarifQuestionsSettingsCard() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({}); // { trade: [{label, unite}] }
  const [saving, setSaving] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/prospect/tarif-questions');
      setTrades(r.data.trades || []);
      const d = {};
      (r.data.trades || []).forEach((t) => { d[t.trade] = t.questions.map((q) => ({ ...q })); });
      setDrafts(d);
    } catch (e) {
      toast.error('Impossible de charger les questions tarifaires');
    } finally { setLoading(false); }
  };

  const setDraft = (trade, questions) => setDrafts((prev) => ({ ...prev, [trade]: questions }));

  const addRow = (trade) => setDraft(trade, [...(drafts[trade] || []), { label: '', unite: '€' }]);
  const removeRow = (trade, i) => {
    const next = [...(drafts[trade] || [])];
    next.splice(i, 1);
    setDraft(trade, next);
  };
  const updateRow = (trade, i, patch) => {
    const next = [...(drafts[trade] || [])];
    next[i] = { ...next[i], ...patch };
    setDraft(trade, next);
  };

  const save = async (trade) => {
    setSaving(trade);
    try {
      const questions = (drafts[trade] || []).filter((q) => (q.label || '').trim());
      await api.put(`/prospect/tarif-questions/${encodeURIComponent(trade)}`, { trade, questions });
      toast.success(`« ${trade} » : ${questions.length} question(s) enregistrée(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la sauvegarde');
    } finally { setSaving(null); }
  };

  const reset = async (trade) => {
    if (!window.confirm(`Réinitialiser « ${trade} » aux questions par défaut ?`)) return;
    setSaving(trade);
    try {
      await api.delete(`/prospect/tarif-questions/${encodeURIComponent(trade)}`);
      toast.success('Réinitialisé aux valeurs par défaut');
      await load();
    } catch (e) {
      toast.error('Échec de la réinitialisation');
    } finally { setSaving(null); }
  };

  if (loading) return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="tarif-questions-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <HardHat className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-900">Questions tarifaires prospects</h2>
          <p className="text-sm text-slate-600 mt-0.5">
            Personnalisez le bordereau tarifaire envoyé aux candidats sous-traitants par corps de métier.
            Les questions par défaut sont affichées grisées — vous pouvez les modifier, en ajouter, ou en supprimer.
          </p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {trades.map((t) => {
          const isOpen = expanded === t.trade;
          const q = drafts[t.trade] || [];
          return (
            <div key={t.trade} className="p-4" data-testid={`tarif-trade-${t.trade}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : t.trade)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <span className="font-semibold text-slate-800 truncate">{t.trade}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{q.length} question{q.length > 1 ? 's' : ''}</span>
                  {t.is_override && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Personnalisé</span>
                  )}
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 space-y-2">
                  {q.map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_100px_auto] gap-2 items-center">
                      <Input
                        value={row.label}
                        onChange={(e) => updateRow(t.trade, i, { label: e.target.value })}
                        placeholder="Libellé (ex : Pose WC suspendu)"
                        data-testid={`tarif-label-${t.trade}-${i}`}
                      />
                      <Input
                        value={row.unite}
                        onChange={(e) => updateRow(t.trade, i, { unite: e.target.value })}
                        placeholder="€/m²"
                        className="text-center"
                        data-testid={`tarif-unite-${t.trade}-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRow(t.trade, i)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                        title="Supprimer"
                        data-testid={`tarif-remove-${t.trade}-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => addRow(t.trade)} data-testid={`tarif-add-${t.trade}`}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter une question
                    </Button>
                    <Button size="sm" onClick={() => save(t.trade)} disabled={saving === t.trade} data-testid={`tarif-save-${t.trade}`}>
                      {saving === t.trade ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Enregistrer
                    </Button>
                    {t.is_override && (
                      <Button variant="outline" size="sm" onClick={() => reset(t.trade)} disabled={saving === t.trade}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
